import { Button, Card, Chip, faDigits, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  accountingApi,
  type Account,
  type AccountCreateInput,
  type AccountLevel,
  type AccountNature,
} from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

const LEVEL_LABELS: Record<AccountLevel, string> = {
  group: "گروه",
  general: "کل",
  subsidiary: "معین",
  detail: "تفصیلی",
};

const NATURE_LABELS: Record<AccountNature, string> = {
  debit: "ماهیت بدهکار",
  credit: "ماهیت بستانکار",
};

export function AccountsTab() {
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("all");

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);

  // Form states for Create
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newLevel, setNewLevel] = useState<AccountLevel>("subsidiary");
  const [newNature, setNewNature] = useState<AccountNature>("debit");
  const [newParentId, setNewParentId] = useState<string>("");

  // Form state for Edit
  const [editName, setEditName] = useState("");

  const accountsQuery = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: async () => accountingApi.listAccounts(),
  });

  const accounts: Account[] = accountsQuery.data ?? [];

  // Filtered accounts
  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      if (levelFilter !== "all" && acc.level !== levelFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        return acc.code.toLowerCase().includes(q) || acc.name.toLowerCase().includes(q);
      }
      return true;
    });
  }, [accounts, levelFilter, search]);

  // Tree view: sort accounts by code
  const sortedAccounts = useMemo(() => {
    return [...filteredAccounts].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  }, [filteredAccounts]);

  // Possible parents for creating a new account
  const possibleParents = useMemo(() => {
    if (newLevel === "group") return [];
    const expectedParentLevel: Record<string, AccountLevel> = {
      general: "group",
      subsidiary: "general",
      detail: "subsidiary",
    };
    const pLevel = expectedParentLevel[newLevel];
    return accounts.filter((a) => a.status === "active" && (!pLevel || a.level === pLevel));
  }, [accounts, newLevel]);

  // Create action
  const createAction = useAction(async () => {
    const input: AccountCreateInput = {
      code: newCode.trim(),
      name: newName.trim(),
      level: newLevel,
      nature: newNature,
      parentId: newParentId || null,
    };
    await accountingApi.createAccount(input);
    setShowAddModal(false);
    setNewCode("");
    setNewName("");
    setNewParentId("");
  }, [["accounting", "accounts"]]);

  // Update action
  const updateAction = useAction(async () => {
    if (!editingAccount || editingAccount.version === undefined) return;
    await accountingApi.updateAccount(editingAccount.id, editingAccount.version, {
      name: editName.trim(),
    });
    setEditingAccount(null);
    setEditName("");
  }, [["accounting", "accounts"]]);

  // Archive action
  const archiveAction = useAction(
    async (acc: Account) => {
      if (acc.version === undefined) return;
      const confirmed = window.confirm(`آیا از بایگانی حساب «${acc.name}» (کد ${acc.code}) اطمینان دارید؟`);
      if (!confirmed) return;
      await accountingApi.archiveAccount(acc.id, acc.version);
    },
    [["accounting", "accounts"]],
  );

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Top Toolbar */}
      <div className="row wrap justify-between items-center" style={{ gap: "var(--s3)" }}>
        <div className="row wrap items-center" style={{ gap: "var(--s3)" }}>
          <input
            type="text"
            className="input"
            placeholder="جستجوی کد یا نام حساب..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            style={{ minWidth: "260px" }}
          />

          <select
            className="select"
            value={levelFilter}
            onChange={(e) => {
              setLevelFilter(e.target.value);
            }}
            style={{ width: "auto" }}
          >
            <option value="all">تمام سطوح حساب</option>
            <option value="group">سطح ۱: گروه</option>
            <option value="general">سطح ۲: کل</option>
            <option value="subsidiary">سطح ۳: معین</option>
            <option value="detail">سطح ۴: تفصیلی</option>
          </select>
        </div>

        <Button
          onClick={() => {
            setShowAddModal(true);
            setNewCode("");
            setNewName("");
            setNewLevel("subsidiary");
            setNewNature("debit");
            setNewParentId("");
          }}
        >
          + تعریف حساب جدید
        </Button>
      </div>

      <ErrorLine error={archiveAction.error} />

      {/* Accounts List / Tree Table */}
      {accountsQuery.isLoading ? (
        <Card>
          <div className="center p-8 text-secondary">در حال بارگذاری سرفصل حساب‌ها...</div>
        </Card>
      ) : sortedAccounts.length === 0 ? (
        <Card>
          <div className="center p-8 text-secondary">حسابی با این مشخصات یافت نشد.</div>
        </Card>
      ) : (
        <div className="table-wrap">
          <table className="table" dir="rtl">
            <thead>
              <tr>
                <th style={{ width: "120px" }}>کد حساب</th>
                <th>نام حساب</th>
                <th style={{ width: "120px" }}>سطح حساب</th>
                <th style={{ width: "140px" }}>ماهیت</th>
                <th style={{ width: "100px" }}>وضعیت</th>
                <th style={{ width: "160px", textAlign: "left" }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {sortedAccounts.map((acc) => {
                const indent =
                  acc.level === "group"
                    ? 0
                    : acc.level === "general"
                      ? 24
                      : acc.level === "subsidiary"
                        ? 48
                        : 72;

                const tone =
                  acc.level === "group"
                    ? "dark"
                    : acc.level === "general"
                      ? "blue"
                      : acc.level === "subsidiary"
                        ? "ok"
                        : "warn";

                return (
                  <tr key={acc.id} className={acc.status === "archived" ? "opacity-60" : ""}>
                    <td className="bold" style={{ fontFamily: "monospace", fontSize: "1.05rem" }}>
                      {faDigits(acc.code)}
                    </td>
                    <td>
                      <div className="row items-center" style={{ paddingRight: `${String(indent)}px` }}>
                        {indent > 0 && <span style={{ opacity: 0.35, marginLeft: "8px" }}>└─</span>}
                        <span className={acc.level === "group" ? "bold" : ""}>{acc.name}</span>
                        {acc.role && (
                          <span style={{ marginRight: "8px" }}>
                            <Chip tone="blue">{acc.role}</Chip>
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <Chip tone={tone}>{LEVEL_LABELS[acc.level]}</Chip>
                    </td>
                    <td>
                      <span className="text-secondary text-sm">{NATURE_LABELS[acc.nature]}</span>
                    </td>
                    <td>
                      {acc.status === "active" ? (
                        <span className="text-sm" style={{ color: "var(--color-success, #2e7d32)" }}>
                          فعال
                        </span>
                      ) : (
                        <span className="text-sm text-secondary">بایگانی</span>
                      )}
                    </td>
                    <td style={{ textAlign: "left" }}>
                      <div className="row justify-end" style={{ gap: "var(--s2)" }}>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setEditingAccount(acc);
                            setEditName(acc.name);
                          }}
                        >
                          ویرایش
                        </Button>
                        {acc.status === "active" && (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              archiveAction.run(acc);
                            }}
                          >
                            بایگانی
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: Add Account */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "540px", width: "100%" }}>
            <Panel title="تعریف حساب جدید در سرفصل">
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <ErrorLine error={createAction.error} />

                <div className="field">
                  <label htmlFor="acc-level">سطح حساب</label>
                  <select
                    id="acc-level"
                    className="select"
                    value={newLevel}
                    onChange={(e) => {
                      const lvl = e.target.value as AccountLevel;
                      setNewLevel(lvl);
                      setNewParentId("");
                    }}
                  >
                    <option value="group">سطح ۱: گروه (۱ رقمی)</option>
                    <option value="general">سطح ۲: کل (۲ رقمی)</option>
                    <option value="subsidiary">سطح ۳: معین (۳ یا ۴ رقمی)</option>
                    <option value="detail">سطح ۴: تفصیلی</option>
                  </select>
                </div>

                {newLevel !== "group" && (
                  <div className="field">
                    <label htmlFor="acc-parent">حساب والد</label>
                    <select
                      id="acc-parent"
                      className="select"
                      value={newParentId}
                      onChange={(e) => {
                        const pid = e.target.value;
                        setNewParentId(pid);
                        const p = accounts.find((a) => a.id === pid);
                        if (p && !newCode.startsWith(p.code)) {
                          setNewCode(p.code);
                        }
                      }}
                    >
                      <option value="">-- انتخاب حساب والد --</option>
                      {possibleParents.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code} — {p.name} ({LEVEL_LABELS[p.level]})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <Field
                  id="acc-code"
                  label="کد حساب"
                  hint="کد حساب فرعی باید با کد والد شروع شود"
                  type="text"
                  placeholder="مثال: ۱۱۶"
                  value={newCode}
                  onChange={(e) => {
                    setNewCode(e.target.value);
                  }}
                  dir="ltr"
                />

                <Field
                  id="acc-name"
                  label="نام حساب"
                  type="text"
                  placeholder="مثال: موجودی ریالی صندوق تبریز"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                  }}
                />

                <div className="field">
                  <label htmlFor="acc-nature">ماهیت حساب</label>
                  <select
                    id="acc-nature"
                    className="select"
                    value={newNature}
                    onChange={(e) => {
                      setNewNature(e.target.value as AccountNature);
                    }}
                  >
                    <option value="debit">بدهکار (دارایی‌ها، بهای تمام شده، هزینه‌ها)</option>
                    <option value="credit">بستانکار (بدهی‌ها، حقوق صاحبان سهام، درآمدها)</option>
                  </select>
                </div>

                <div className="row justify-end" style={{ gap: "var(--s3)", marginTop: "var(--s4)" }}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowAddModal(false);
                    }}
                  >
                    انصراف
                  </Button>
                  <Button
                    onClick={() => {
                      createAction.run(undefined);
                    }}
                    disabled={createAction.busy || !newCode.trim() || !newName.trim()}
                  >
                    {createAction.busy ? "در حال ثبت..." : "ثبت حساب"}
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* Modal: Edit Account Name */}
      {editingAccount && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: "480px", width: "100%" }}>
            <Panel title={`ویرایش حساب ${editingAccount.code}`}>
              <div className="stack" style={{ gap: "var(--s4)" }}>
                <ErrorLine error={updateAction.error} />

                <Field
                  id="edit-acc-name"
                  label="نام جدید حساب"
                  type="text"
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                  }}
                />

                <div className="row justify-end" style={{ gap: "var(--s3)", marginTop: "var(--s4)" }}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditingAccount(null);
                    }}
                  >
                    انصراف
                  </Button>
                  <Button
                    onClick={() => {
                      updateAction.run(undefined);
                    }}
                    disabled={updateAction.busy || !editName.trim()}
                  >
                    {updateAction.busy ? "در حال ذخیره..." : "ذخیره تغییرات"}
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
